import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Lock, User } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../lib/api';
const SITE_LOGO = "https://customer-assets.emergentagent.com/job_9dd50a71-7bb8-45b9-bdec-566056107363/artifacts/kgdjrixe_Untitled%20design.png";

const AdminLogin = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!username || !password) {
      toast.error('Please enter both username and password');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, {
        username,
        password
      });
      
      const { access_token } = response.data;
      localStorage.setItem('admin_token', access_token);
      toast.success('Login successful');
      navigate('/admin/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <img 
            src={SITE_LOGO} 
            alt="What's Happening Baltimore" 
            className="h-16 mx-auto mb-6"
          />
          <h1 className="font-outfit text-3xl font-bold text-white mb-2">Admin Login</h1>
          <p className="text-zinc-500">Sign in to manage your content</p>
        </div>

        <form 
          onSubmit={handleLogin}
          data-testid="admin-login-form"
          className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8"
        >
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">
                Username
              </label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" size={20} />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  data-testid="admin-username-input"
                  placeholder="Enter username"
                  className="w-full bg-black/50 border border-zinc-700 text-white placeholder-zinc-500 pl-12 pr-4 py-3 rounded-xl focus:outline-none focus:border-purple-500 transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" size={20} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="admin-password-input"
                  placeholder="Enter password"
                  className="w-full bg-black/50 border border-zinc-700 text-white placeholder-zinc-500 pl-12 pr-12 py-3 rounded-xl focus:outline-none focus:border-purple-500 transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-btn"
              className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-purple-500/50 text-white py-3 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                'Sign In'
              )}
            </button>
          </div>
        </form>

        <p className="text-center text-zinc-600 text-sm mt-6">
          <a href="/" className="text-zinc-500 hover:text-purple-500 transition-colors">
            ← Back to Website
          </a>
        </p>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
